import { ConfigInterface } from "@traas/lib/src/config";
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

export type RealtimeConnectionStatus = "connecting" | "online" | "offline";
export type RealtimeMessage = { type: string; [key: string]: any };
export type RealtimeMessageHandler = (message: RealtimeMessage) => void;

interface RealtimeContextType {
  status: RealtimeConnectionStatus;
  sessionId: string | null;
  send: (message: object) => void;
  subscribe: (handler: RealtimeMessageHandler) => () => void;
}

// Server pingt elke 5sec. Na 15sec moeten we dus echt wel wat gehoord hebben.
const WATCHDOG_TIMEOUT_MS = 15000;
const RECONNECT_DELAY_MS = 2000;

const RealtimeContext = createContext<RealtimeContextType>({} as RealtimeContextType);

interface RealtimeContextProviderProps {
  tokenResolver: () => string | null;
  config: ConfigInterface;
  children: React.ReactNode;
}
export const RealtimeContextProvider: FC<RealtimeContextProviderProps> = ({ children, config, tokenResolver }) => {
  const [status, setStatus] = useState<RealtimeConnectionStatus>("connecting");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const handlers = useRef(new Set<RealtimeMessageHandler>());
  const connectionRef = useRef<WebSocket | null>(null);
  const tokenResolverRef = useRef(tokenResolver);
  tokenResolverRef.current = tokenResolver;

  useEffect(() => {
    let stopped = false;
    let reconnectTimer: ReturnType<typeof setTimeout>;
    let watchdogTimer: ReturnType<typeof setTimeout>;

    const connect = () => {
      setStatus("connecting");
      const connection = new WebSocket(config.realtimeApiUrl);
      connectionRef.current = connection;

      const close = () => {
        clearTimeout(watchdogTimer);
        setStatus("offline");
        setSessionId(null);
        if (!stopped) {
          reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
        }
      };

      const resetWatchdog = () => {
        clearTimeout(watchdogTimer);
        watchdogTimer = setTimeout(() => {
          connection.close();
          connection.onclose = null;
          connectionRef.current = null;
          close();
        }, WATCHDOG_TIMEOUT_MS);
      };

      connection.onopen = () => {
        connection.send(JSON.stringify({ type: "login", token: tokenResolverRef.current() }));
        resetWatchdog();
      };
      connection.onmessage = (e) => {
        resetWatchdog();
        const message: RealtimeMessage = JSON.parse(e.data || "{}");
        switch (message.type) {
          case undefined:
            return;
          case "hello":
            setStatus("online");
            setSessionId(message.sessionId);
            return;
          case "marco":
            connection.send(JSON.stringify({ type: "polo" }));
            return;
          case "ping":
            connection.send(JSON.stringify({ type: "pong" }));
            return;
        }
        handlers.current.forEach((handler) => handler(message));
      };
      connection.onclose = () => {
        close();
      };
    };

    connect();
    // Cleanup/unmount
    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      clearTimeout(watchdogTimer);
      connectionRef.current?.close();
    };
  }, [config.realtimeApiUrl]);

  const send = useCallback((message: object) => {
    if (connectionRef.current?.readyState === WebSocket.OPEN) {
      connectionRef.current.send(JSON.stringify(message));
    } else {
      console.warn("Websocket not open, not sending message", connectionRef.current, message);
    }
  }, []);

  const subscribe = useCallback((handler: RealtimeMessageHandler) => {
    handlers.current.add(handler);
    return () => {
      handlers.current.delete(handler);
    };
  }, []);

  // Exposed context
  const context: RealtimeContextType = useMemo(
    () => ({
      status,
      sessionId,
      send,
      subscribe,
    }),
    [status, sessionId, send, subscribe],
  );
  return <RealtimeContext.Provider value={context}>{children}</RealtimeContext.Provider>;
};

export const useRealtimeConnection = () => useContext(RealtimeContext);

export const useRealtimeMessages = (handler: RealtimeMessageHandler) => {
  const { subscribe } = useRealtimeConnection();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => subscribe((message) => handlerRef.current(message)), [handlerRef]);
};
